import React from "react";
import {
  FaGlobe,
  FaAward,
  FaHandshake,
  FaEye,
  FaBullseye,
  FaHeart,
  } from "react-icons/fa";
import "../Styles/About.css";
import { Helmet } from "react-helmet";
import { PageHero, CtaBand } from "./PageParts";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";

export default function About() {
  return (
    <div className="about-page ve-scope">
      <Helmet>
        <title>About VE Abroad | 10+ Years of Study & Migration Expertise</title>
        <meta name="description" content="Learn about VE Abroad, Mumbai's top study abroad & immigration consultants. 10+ years in global migration & 35+ years in academic coaching. Read our story!" />
        <link rel="canonical" href="https://veabroad.com/about" />
        <meta property="og:title" content="About VE Abroad | 10+ Years of Study & Migration Expertise" />
        <meta property="og:description" content="Learn about VE Abroad, Mumbai's top study abroad & immigration consultants. 10+ years in global migration & 35+ years in academic coaching." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://veabroad.com/about" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About VE Abroad | 10+ Years of Study & Migration Expertise" />
        <meta name="twitter:description" content="Learn about VE Abroad, Mumbai's top study abroad & immigration consultants. 10+ years in global migration & 35+ years in academic coaching." />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About VE Abroad",
            "description": "VE Abroad is a trusted education and migration consultancy with over a decade of expertise.",
            "mainEntity": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            }
          })}
        </script>
      </Helmet>

      {/* Decorative Background Blobs */}
      <div className="about-bg-blob about-blob-1"></div>
      <div className="about-bg-blob about-blob-2"></div>
      <div className="about-bg-blob about-blob-3"></div>

      {/* Hero Section */}
      <PageHero
        image="/OfficeCabin2.webp"
        eyebrow="About VE Abroad"
        title={<>Thirty-five years of <em>sitting across the table</em> from students.</>}
        lede="An education and migration consultancy in Andheri West, Mumbai, built on an academic coaching practice that started in 1989 — which is why our counselling still begins with your marks and your family budget rather than a brochure."
        ctas={[
          { to: "/consultationForm", label: "Book a consultation" },
          { to: "/our-office", label: "Visit our office", variant: "ghost" },
        ]}
        stats={[
          { figure: "35+", label: "Years of academic coaching" },
          { figure: "10+", label: "Years in migration" },
          { figure: "500+", label: "Student & PR approvals" },
        ]}
      />

      {/* The story that used to be crammed into the hero. */}
      <section className="ve-section about-story">
        <div className="ve-container about-story__grid">
          <div className="about-story__aside">
            <span className="ve-eyebrow">Who we are</span>
            <h2 className="ve-h2">Built on teaching, not on commissions.</h2>
          </div>

          <div className="about-story__body">
            <p>
              Our journey began in education. More than three decades of academic
              mentoring taught us what students actually struggle with — and how much
              preparation a competitive global application really takes.
            </p>
            <p>
              VE Abroad was established on that foundation to provide structured,
              transparent and result-driven pathways for students seeking higher
              education abroad and professionals aiming for international migration.
            </p>
            <p>
              Over the years we have guided 500+ students and families toward
              international education, Permanent Residency and work permit pathways
              across Canada and other leading destinations — with personalised
              counselling, compliance-focused documentation and end-to-end support
              from university selection through to post-landing assistance.
            </p>
            <p>
              We operate alongside our sister concern, Liba Migration Services, a
              recognised name in Canada PR and work permit solutions. Together we cover
              the full range of global mobility for students, skilled professionals and
              families settling long term.
            </p>

            <ul className="about-story__points">
              {[
                "10+ years of international migration expertise",
                "35+ years of academic coaching experience",
                "500+ successful student and PR approvals",
                "Transparent, ethical advisory — fees quoted in writing",
                "A strong global network of partner institutions",
              ].map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="about-section light-bg">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-6">
              <Reveal direction="left" duration={0.6} className="mv-card glass-card va-hover-lift">
                <div className="mv-icon-box">
                  <FaBullseye />
                </div>
                <h3>Our Mission</h3>
                <p>
                  To deliver accurate, transparent, and compliance-driven
                  migration and education services. To provide personalized
                  career strategies aligned with each client's academic
                  background and long-term goals. To maintain the highest
                  standards of integrity, professionalism, and ethical
                  counselling. To build long-term relationships based on trust,
                  results, and global success. To empower students and families
                  with the right information, preparation, and support required
                  for international settlement.
                </p>
              </Reveal>
            </div>
            <div className="col-lg-6">
              <Reveal direction="right" duration={0.6} delay={0.15} className="mv-card glass-card va-hover-lift">
                <div className="mv-icon-box">
                  <FaEye />
                </div>
                <h3>Our Vision</h3>
                <p>
                  To become a globally recognized education and migration
                  advisory brand that transforms aspirations into international
                  success stories — by providing ethical guidance, strategic
                  planning, and life-changing global opportunities. We envision
                  a future where students and professionals from India
                  confidently access world-class education, meaningful careers,
                  and secure residency pathways across the globe through trusted
                  and transparent mentorship.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="about-section">
        <div className="va-blob-field">
          <span className="va-blob va-blob-teal" style={{ width: 320, height: 320, top: "-8%", right: "-6%" }} />
          <span className="va-blob va-blob-mint" style={{ width: 260, height: 260, bottom: "-10%", left: "-4%" }} />
        </div>
        <div className="container position-relative z-2">
          <Reveal direction="up" className="section-header text-center">
            <div>
              <span className="section-badge">Values</span>
            </div>
            <h2 className="section-title">Our Core Principles</h2>
            <p className="section-subtitle mx-auto">We operate under a strict code of ethics to deliver the best quality outcomes for all our clients.</p>
          </Reveal>
          <RevealGroup as="div" className="values-grid" stagger={0.12}>
            {[
              { icon: <FaHandshake />, title: "Integrity", desc: "We maintain the highest ethical standards in all our dealings, ensuring transparency and honesty at every step." },
              { icon: <FaHeart />, title: "Compassion", desc: "We understand that each journey is personal and treat every client with empathy, care, and respect." },
              { icon: <FaAward />, title: "Excellence", desc: "We strive for perfection in everything we do, continuously improving our services and expertise." },
              { icon: <FaGlobe />, title: "Innovation", desc: "We embrace new technologies and methodologies to provide cutting-edge solutions for our clients." },
            ].map((value, idx) => (
              <RevealItem
                key={idx}
                direction="up"
                className="value-card glass-card va-hover-lift"
              >
                <div className="value-icon-box">
                  {value.icon}
                </div>
                <h4>{value.title}</h4>
                <p>{value.desc}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="about-section light-bg">
        <div className="container">
          <Reveal direction="up" className="section-header text-center">
            <div>
              <span className="section-badge">Leadership</span>
            </div>
            <h2 className="section-title">The Visionaries Behind VE Abroad</h2>
            <p className="section-subtitle mx-auto">Combining 35+ years of premium academic coaching with elite global migration strategy.</p>
          </Reveal>
          <div className="row g-5">
            <div className="col-lg-6">
              <Reveal
                direction="left"
                duration={0.6}
                className="leader-card glass-card va-hover-lift"
              >
                <div className="leader-header">
                  <h3>Mr. Girish Gupta</h3>
                  <p className="leader-title">
                    Co-Founder & Director – Academic Strategy & Career Development
                  </p>
                </div>
                <div className="leader-content">
                  <p>
                    With an exceptional legacy of over 35 years in education
                    coaching and career mentorship, Mr. Girish Gupta stands as
                    the academic pillar behind VE Abroad. His distinguished
                    career has been dedicated to shaping futures, mentoring
                    young minds, and building strong academic foundations that
                    lead to global success.
                  </p>
                  <p>
                    Renowned for his strategic career counselling approach, Mr.
                    Gupta has guided thousands of students in identifying the
                    right academic pathways aligned with long-term career
                    growth. His deep understanding of education systems, student
                    psychology, and international academic frameworks enables
                    him to provide clarity where students often face
                    uncertainty.
                  </p>
                  <p>
                    Through his visionary leadership, more than 100+ students
                    have successfully settled abroad through education and
                    international work opportunities. His mentorship extends
                    beyond admissions — he believes in preparing students for
                    life, leadership, and long-term global success.
                  </p>
                  <p>
                    At VE Abroad, Mr. Gupta ensures that every student
                    receives guidance rooted in experience, integrity, and
                    excellence — maintaining the highest standards of academic
                    and ethical counselling.
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="col-lg-6">
              <Reveal
                direction="right"
                duration={0.6}
                delay={0.15}
                className="leader-card glass-card va-hover-lift"
              >
                <div className="leader-header">
                  <h3>Mr. Jafar Sayed</h3>
                  <p className="leader-title">
                    Founder & Managing Director – Liba Migration Services
                    <br />
                    Co-Founder & Director – Global Migration & International Relations, VE Abroad
                  </p>
                </div>
                <div className="leader-content">
                  <p>
                    Mr. Jafar Sayed is a visionary entrepreneur and
                    international migration specialist with over 8 years of
                    proven expertise in global immigration services. As the
                    Founder of Liba Migration Services and Co-Founder of
                    VE Abroad, he leads the organization's international
                    expansion, strategic partnerships, and high-value
                    immigration programs.
                  </p>
                  <p>
                    With extensive experience across multiple immigration
                    systems, Mr. Sayed specializes in Canada Permanent Residency
                    (PR), Work Permits, Study Visas, Business Immigration, and
                    Skilled Migration Programs. His deep procedural knowledge,
                    regulatory understanding, and strategic documentation
                    planning have helped numerous families establish successful
                    lives in Canada and enabled students to access world-class
                    education globally.
                  </p>
                  <p>
                    Under his leadership, Liba Migration Services has earned a
                    reputation for transparency, precision, and results-driven
                    execution. He is known for building structured migration
                    strategies tailored to each client's profile — ensuring
                    long-term stability, compliance, and success.
                  </p>
                  <p>
                    His mission is not just migration — it is global mobility
                    with purpose, helping families, professionals, and students
                    build secure and prosperous futures abroad.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="about-section">
        <div className="va-grid-overlay" />
        <div className="container position-relative z-2">
          <Reveal direction="up" className="section-header text-center">
            <div>
              <span className="section-badge">Milestones</span>
            </div>
            <h2 className="section-title">Our Achievements</h2>
            <p className="section-subtitle mx-auto">A proven track record of excellence in assisting families and students to move abroad.</p>
          </Reveal>
          <RevealGroup as="div" className="achievements-grid" stagger={0.1}>
            {[
              { number: "1000+", label: "Successful Cases" },
              { number: "50+", label: "Countries Covered" },
              { number: "98%", label: "Success Rate" },
              { number: "35+", label: "Years Experience" },
            ].map((ach, idx) => (
              <RevealItem
                key={idx}
                direction="zoom"
                className="achievement-card glass-card va-hover-lift"
              >
                <div className="achievement-number va-pop">{ach.number}</div>
                <div className="achievement-label">{ach.label}</div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Certifications */}
      <section className="about-section light-bg">
        <div className="container">
          <Reveal direction="up" className="section-header text-center">
            <div>
              <span className="section-badge">Accreditation</span>
            </div>
            <h2 className="section-title">Certifications & Memberships</h2>
            <p className="section-subtitle mx-auto">Fully regulated and compliant with global immigration governing bodies.</p>
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.1}>
            {[
              { title: "ICCRC Certified", desc: "Immigration Consultants of Canada Regulatory Council" },
              { title: "MARA Registered", desc: "Migration Agents Registration Authority - Australia" },
              { title: "OISC Regulated", desc: "Office of the Immigration Services Commissioner - UK" },
              { title: "ISO Certified", desc: "ISO 9001:2015 Quality Management System" },
            ].map((cert, idx) => (
              <div className="col-lg-3 col-md-6" key={idx}>
                <RevealItem direction="up" className="cert-card glass-card va-hover-lift">
                  <div className="cert-icon-box">
                    <FaAward />
                  </div>
                  <h5>{cert.title}</h5>
                  <p>{cert.desc}</p>
                </RevealItem>
              </div>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA Section */}
      <CtaBand
        title="Come and sit with us in Andheri West."
        body="The first consultation is free and genuinely no-obligation. Bring your marksheets, your questions and your budget — we will tell you honestly where you stand."
        primary={{ to: "/consultationForm", label: "Book a consultation" }}
        secondary={{ to: "/our-office", label: "Find our office" }}
      />
    </div>
  );
}
